import { ReactNode, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import LanguageToggle from "@/components/LanguageToggle";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, ShieldCheck, DollarSign, BarChart3,
  Menu, X, LogOut, ChevronRight,
} from "lucide-react";

interface AdminLayoutProps {
  children: ReactNode;
}

const navItems = [
  { path: "/admin", icon: LayoutDashboard, labelEn: "Overview", labelBn: "ওভারভিউ", end: true },
  { path: "/admin/verification", icon: ShieldCheck, labelEn: "Verification", labelBn: "যাচাইকরণ", end: false },
  { path: "/admin/revenue", icon: DollarSign, labelEn: "Revenue", labelBn: "রাজস্ব", end: false },
  { path: "/admin/analytics", icon: BarChart3, labelEn: "Analytics", labelBn: "বিশ্লেষণ", end: false },
];

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const { language } = useLanguage();
  const { logout } = useAuth();
  const bn = language === "bn";
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const getActiveLabel = () => {
    const match = navItems.find((i) =>
      i.end ? location.pathname === i.path : location.pathname.startsWith(i.path) && i.path !== "/admin"
    );
    return match || navItems[0];
  };

  const SidebarContent = ({ onClose }: { onClose?: () => void }) => (
    <>
      <div className="p-5 border-b border-border/30">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Sebaco" className="h-10 w-10 rounded-2xl object-contain" />
          <div>
            <h2 className="font-extrabold text-sm text-foreground tracking-tight">Sebaco</h2>
            <p className="text-[10px] text-primary font-bold uppercase tracking-wider">Admin Console</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1.5">
        {navItems.map((item) => (
          <NavLink key={item.path} to={item.path} end={item.end} onClick={onClose}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all group ${
              isActive
                ? "bg-primary text-primary-foreground shadow-glow"
                : "text-muted-foreground hover:bg-muted/20 hover:text-foreground"
            }`}>
            {({ isActive }) => (
              <>
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all ${
                  isActive
                    ? "bg-primary-foreground/20"
                     : "bg-[#e4f1eb] group-hover:bg-[#d8f2ed]"
                }`}>
                  <item.icon className={`h-4 w-4 ${isActive ? "text-primary-foreground" : "text-primary"}`} />
                </div>
                <span className="flex-1">{bn ? item.labelBn : item.labelEn}</span>
                <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border/30 space-y-2">
        <div className="flex items-center gap-3 px-4 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#e4f1eb] text-sm font-extrabold text-primary">A</div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-extrabold truncate">Admin</p>
            <p className="text-[10px] text-muted-foreground">{bn ? "সুপার অ্যাডমিন" : "Super Admin"}</p>
          </div>
        </div>
        <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-destructive w-full hover:bg-destructive/10 transition-colors">
          <LogOut className="h-4 w-4" />{bn ? "লগআউট" : "Logout"}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-card/50 backdrop-blur-sm border-r border-border/30 fixed h-full z-40">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div className="fixed inset-0 bg-foreground/30 backdrop-blur-sm z-40 md:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSidebarOpen(false)} />
            <motion.aside className="fixed left-0 top-0 h-full w-72 bg-card z-50 flex flex-col md:hidden shadow-2xl" initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "spring", damping: 25 }}>
              <div className="absolute right-3 top-3">
                <button onClick={() => setSidebarOpen(false)} className="h-8 w-8 rounded-lg bg-muted/30 flex items-center justify-center">
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
              <SidebarContent onClose={() => setSidebarOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="flex-1 md:ml-64">
        <header className="md:hidden sticky top-0 z-30 bg-card/80 backdrop-blur-xl border-b border-border/30 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="h-9 w-9 rounded-lg bg-muted/30 flex items-center justify-center">
            <Menu className="h-4 w-4 text-foreground" />
          </button>
          <h1 className="font-extrabold text-sm tracking-tight">{bn ? getActiveLabel().labelBn : getActiveLabel().labelEn}</h1>
          <LanguageToggle />
        </header>
        <header className="hidden md:flex sticky top-0 z-30 bg-card/50 backdrop-blur-xl border-b border-border/30 px-6 py-4 items-center justify-between">
          <div>
            <h1 className="font-extrabold text-lg tracking-tight">{bn ? getActiveLabel().labelBn : getActiveLabel().labelEn}</h1>
            <p className="text-xs text-muted-foreground">{bn ? "সেবাকো ম্যানেজমেন্ট" : "Sebaco Management"}</p>
          </div>
          <LanguageToggle />
        </header>
        <main className="p-4 md:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
